import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import _ from 'lodash'
import { VList } from 'virtua'

import { observer } from 'mobx-react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import Category from 'modules/reports/components/Editor/FieldSelectorModal/Category'
import Field from 'modules/reports/components/Editor/FieldSelectorModal/Field'
import FilterForm from 'modules/reports/components/Editor/FieldSelectorModal/FilterForm'
import Subcategory from 'modules/reports/components/Editor/FieldSelectorModal/Subcategory'
import {
  categoryNodeTypes,
  dispatcherCategoryNodeType,
} from 'modules/reports/hooks/useCategoryNodes'
import { fieldsCategories } from 'modules/reports/lib/muncher/fieldsGrouper'
import { default as FieldModel } from 'modules/reports/store/models/Field'

import styles from './../index.module.css'

type Props = {
  search: string
  fields: FieldModel[]
  selectedFields: FieldModel[]
  selected: string[]
  onClickItem: (field: FieldModel) => void
  categoryNodes: categoryNodeTypes
  nodeDispatcher: dispatcherCategoryNodeType
  disabledTables: Set<string>
  nonRelatedFields: string[]
}

const List = ({
  search,
  fields,
  selectedFields,
  selected,
  onClickItem,
  categoryNodes,
  nodeDispatcher,
  nonRelatedFields,
  disabledTables,
}: Props) => {
  const list = useUnsafeMemo(() => {
    const filterForm: FilterForm = new FilterForm({})

    return filterForm.applyFilters(fields, {
      selected: [],
      search,
    })
  }, [search, fields])

  const addNode = useUnsafeCallback(
    (category: string, subcategory?: string) => {
      nodeDispatcher({ type: 'ADD', value: { category, subcategories: _.compact([subcategory]) } })
    },
    [nodeDispatcher]
  )

  const deleteNode = useUnsafeCallback(
    (category: string, subcategory: string) => {
      nodeDispatcher({ type: 'DELETE', value: { category, subcategories: [subcategory] } })
    },
    [nodeDispatcher]
  )

  const findNode = useUnsafeCallback(
    (category: string, subcategory?: string): boolean => {
      // the order of these checks its important, do not change!
      if (subcategory) {
        return !!categoryNodes[category]?.has('root') && !!categoryNodes[category]?.has(subcategory)
      }

      if (category) {
        return !!categoryNodes[category]?.has('root')
      }

      return false
    },
    [categoryNodes]
  )

  const searchNodes = useUnsafeCallback(() => {
    nodeDispatcher({ type: 'FLUSH' })

    const result = fieldsCategories(list)

    Object.entries(result).forEach(([category, subcategories]) => {
      nodeDispatcher({ type: 'ADD', value: { category, subcategories } })
    })
  }, [nodeDispatcher, list])

  useUnsafeEffect(() => {
    if (search.length) {
      searchNodes()
    }
  }, [searchNodes, search])

  const selection = useUnsafeMemo(
    () => [...selected, ...nonRelatedFields],
    [selected, nonRelatedFields]
  )

  const listToRender = useUnsafeMemo(() => {
    const visibleNodes: Record<string, Set<string>> = {}

    const isCategoryVisible = (label?: string) => {
      if (!label) return false

      const result = visibleNodes[label]

      if (!result) {
        visibleNodes[label] = new Set()
      }

      return !!result
    }

    const isSubcategoryVisible = (label?: string, subcategory?: string) => {
      if (!label || !subcategory) return false

      const result = visibleNodes[label]?.has(subcategory)

      if (!result) {
        visibleNodes[label]?.add(subcategory)
      }

      return !!result
    }

    return _.compact(
      list.flatMap((field) => {
        const { category_label: category, subcategory_label: subcategory } = field.attributes
        const table = field.table // Use the getter from the Field model

        if (_.isNil(category) || _.isNil(subcategory)) return

        return [
          !isCategoryVisible(category) &&
            (() => {
              return (
                <Category
                  disabled={disabledTables.has(table)}
                  key={`category_${field.attributes.category_label}`}
                  title={category}
                  open={findNode(category)}
                  onClose={() => {
                    deleteNode(category, 'root')
                  }}
                  onOpen={() => {
                    addNode(category)
                  }}
                />
              )
            }),
          subcategory != 'root' &&
            !isSubcategoryVisible(category, subcategory) &&
            findNode(category) &&
            (() => (
              <Subcategory
                key={`subcategory_${category}_${subcategory}`}
                open={findNode(category, subcategory)}
                onClose={() => {
                  deleteNode(category, subcategory)
                }}
                onOpen={() => {
                  addNode(category, subcategory)
                }}
                label={subcategory}
              />
            )),
          findNode(category, field.attributes.subcategory_label) &&
            (() => (
              <Box>
                <Field
                  disabled={disabledTables.has(table)}
                  key={`field_${field.attributes.path}`}
                  field={field}
                  selection={selection}
                  onClickItem={() => onClickItem(field)}
                  selectedFields={selectedFields}
                />
              </Box>
            )),
        ].filter(Boolean)
      })
    )
  }, [addNode, deleteNode, disabledTables, findNode, list, onClickItem, selectedFields, selection])

  if (!list.length)
    return <div className={styles.blank}>{i18n.t('custom_reports.editor.no_matching_fields')}</div>

  return (
    <VList count={listToRender.length}>
      {(i) => {
        const item = listToRender[i]

        if (_.isNil(item)) throw 'Missing item'

        return item()
      }}
    </VList>
  )
}

export default observer(List)
